// src/utils/datetime.js
// ===== 方針 =====
// - event_date (DATE), open_time/start_time (TIME) はJSTの“壁時計”文字列として扱う（変換しない）
// - event_end_at は絶対時刻。API/JSONは ISO 8601 UTC (Z) で入出力する。
// - フロントは：入力はJST（datetime-local）で受け、送信前に UTC(Z) へ変換。受信は UTC→JST 表示。
// - 変換はこのユーティリティのみで行う（各画面で独自実装しない）。

const JST_TZ = 'Asia/Tokyo';
const JST_OFFSET_MIN = 9 * 60; // JST=UTC+09:00
const pad2 = (n) => String(n).padStart(2, '0');

// ─────────────────────────────────────────────
// 基本整形（DATE/TIMEは“壁時計”のまま扱う：シフトしない）
// ─────────────────────────────────────────────

/** 'YYYY-MM-DD' を安全にそのまま抽出（Dateに通さない） */
export const toDateInput = (raw) => {
  if (!raw) return '';
  const s = String(raw).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  return '';
};

/** 'HH:mm[:ss]' の先頭 'HH:mm' を抽出（Dateに通さない） */
export const toTimeInput = (raw) => {
  if (!raw) return '';
  const s = String(raw).trim();
  const m = s.match(/(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : '';
};

/** 'YYYY-MM-DD' → 'YYYY年M月D日'（壁時計・シフトなし） */
export const formatDateJP = (val) => {
  if (!val) return '';
  const m = String(val).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  const [, y, M, d] = m;
  return `${Number(y)}年${Number(M)}月${Number(d)}日`;
};

/** 'HH:mm[:ss]' → 'HH:mm' */
export const toHHmm = (val) => {
  if (!val) return '';
  const m = String(val).trim().match(/(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : '';
};

/** 既にローカル前提の 'YYYY-MM-DD HH:mm' / 'YYYY-MM-DDTHH:mm' を datetime-local 形式へ（シフトなし） */
export const toDatetimeLocalNoShift = (raw) => {
  if (!raw) return '';
  const s = String(raw).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (!m) return '';
  return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}`;
};

// ─────────────────────────────────────────────
// JST <-> UTC(Z) 変換（絶対時刻用）
// ─────────────────────────────────────────────

/** IntlでDate→JST 'YYYY/MM/DD HH:mm' を作る */
export const dateToYmdHmJST = (date) => {
  const parts = new Intl.DateTimeFormat('ja-JP', {
    timeZone: JST_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false
  }).formatToParts(date);

  const take = (type) => parts.find(p => p.type === type)?.value ?? '';
  const yyyy = take('year');
  const MM   = take('month');
  const dd   = take('day');
  const HH   = take('hour');
  const mm   = take('minute');
  return `${yyyy}/${MM}/${dd} ${HH}:${mm}`;
};

/** IntlでDate→JST 'YYYY/MM/DD HH:mm:ss' を作る */
export const dateToYmdHmsJST = (date) => {
  const parts = new Intl.DateTimeFormat('ja-JP', {
    timeZone: JST_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
  }).formatToParts(date);

  const take = (type) => parts.find(p => p.type === type)?.value ?? '';
  const yyyy = take('year');
  const MM   = take('month');
  const dd   = take('day');
  const HH   = take('hour');
  const mm   = take('minute');
  const ss   = take('second');
  return `${yyyy}/${MM}/${dd} ${HH}:${mm}:${ss}`;
};

/** ISO(UTC/オフセット付き) → JSTの 'YYYY-MM-DDTHH:mm'（datetime-local 用） */
export const isoToDatetimeLocalJST = (isoLike) => {
  if (!isoLike) return '';
  const d = new Date(isoLike);
  if (Number.isNaN(d.getTime())) return '';

  const parts = new Intl.DateTimeFormat('ja-JP', {
    timeZone: JST_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false
  }).formatToParts(d);

  const take = (type) => parts.find(p => p.type === type)?.value ?? '';
  const yyyy = take('year');
  const MM   = take('month');
  const dd   = take('day');
  const HH   = take('hour');
  const mm   = take('minute');
  return `${yyyy}-${MM}-${dd}T${HH}:${mm}`;
};

/** 後方互換のため：以前の isoToLocalDatetime を JST固定版にエイリアス */
export const isoToLocalDatetime = isoToDatetimeLocalJST;

/** 'YYYY-MM-DDTHH:mm[:ss]'（オフセットなし＝JST壁時計）→ UTC ISO(Z) */
export const jstDatetimeLocalToUtcIso = (localLike) => {
  if (!localLike) return '';
  const s = String(localLike).trim();
  const m = s.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/
  );
  if (!m) return '';

  const y = Number(m[1]);
  const M = Number(m[2]);
  const d = Number(m[3]);
  const h = Number(m[4]);
  const i = Number(m[5]);
  const sec = Number(m[6] ?? '0');

  // JST壁時計 → UTCエポック（JST=UTC+9h なので 9h引く）
  const epochUtcMs = Date.UTC(y, M - 1, d, h, i, sec) - JST_OFFSET_MIN * 60 * 1000;
  return new Date(epochUtcMs).toISOString();
};

/**
 * DBの event_end_at（UTCのtimestamptz or オフセット付ISO文字列）を
 * 'YYYY/MM/DD HH:mm'（JST）で表示。
 * - Z/±HH:mm のオフセットが付いている場合のみ Date に通してJST表示
 * - 'YYYY-MM-DD[ T]HH:mm' のようなTZなしは “壁時計JST” としてそのまま表示
 */
export const formatDateTimeJST = (val) => {
  if (!val) return '';
  const s = String(val).trim();

  // オフセット/Zあり → Dateに通してJST表示
  if (/[+-]\d{2}:\d{2}$/.test(s) || /Z$/i.test(s)) {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return dateToYmdHmJST(d);
    return '';
  }

  // TZなし： 'YYYY-MM-DD[ T]HH:mm[:ss]' を壁時計JSTとしてそのまま表示
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (m) {
    const [, yyyy, MM, dd, HH, mm] = m;
    return `${yyyy}/${MM}/${dd} ${HH}:${mm}`;
  }

  return '';
};

/**
 * formatDateTimeJST の秒付き版。
 */
export const formatDateTimeJSTWithSeconds = (val) => {
  if (!val) return '';
  const s = String(val).trim();

  if (/[+-]\d{2}:\d{2}$/.test(s) || /Z$/i.test(s)) {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return dateToYmdHmsJST(d);
    return '';
  }

  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (m) {
    const [, yyyy, MM, dd, HH, mm, ss] = m;
    return `${yyyy}/${MM}/${dd} ${HH}:${mm}:${pad2(ss ?? '0')}`;
  }

  return '';
};

/**
 * 任意の日時文字列（ISO Z/±HH:mm または TZなし 'YYYY-MM-DD[ T]HH:mm(:ss)?'、日付のみ、スラッシュ区切りも可）
 * を JST で『YYYY年M月D日 HH:mm』に整形。
 * - オフセット付き or Z：Dateに通してJSTに変換
 * - TZなし：壁時計JSTとしてそのまま整形
 * - 日付のみ：『YYYY年M月D日』で返す
 */
export const formatDateJPFromAny = (val) => {
  if (!val) return '';
  const s = String(val).trim();

  // 1) ISO(オフセット/Z) → JSTへ
  if (/[+-]\d{2}:\d{2}$/.test(s) || /Z$/i.test(s)) {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return '';
    const parts = new Intl.DateTimeFormat('ja-JP', {
      timeZone: JST_TZ,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(d);
    const take = (t) => parts.find(p => p.type === t)?.value ?? '';
    const yyyy = Number(take('year'));
    const M    = Number(take('month'));
    const D    = Number(take('day'));
    const HH   = (take('hour') || '').padStart(2, '0');
    const mm   = (take('minute') || '').padStart(2, '0');
    return `${yyyy}年${M}月${D}日 ${HH}:${mm}`;
  }

  // 2) TZなし 'YYYY-MM-DD[ T]HH:mm(:ss)?' or 'YYYY/MM/DD HH:mm'
  let m = s.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::\d{2})?)?$/);
  if (m) {
    const yyyy = Number(m[1]);
    const M    = Number(m[2]);
    const D    = Number(m[3]);
    const HH   = m[4] != null ? String(m[4]).padStart(2, '0') : null;
    const mm   = m[5] != null ? String(m[5]).padStart(2, '0') : null;
    return HH && mm
      ? `${yyyy}年${M}月${D}日 ${HH}:${mm}`
      : `${yyyy}年${M}月${D}日`;
  }

  // 3) 不明形式は空
  return '';
};

export const formatDateOnlyJST = (val, fallback = '') => {
  if (!val) return fallback;
  const ymd = toDateInput(val);
  if (ymd) return ymd.replaceAll('-', '/');
  const dateTime = formatDateTimeJST(val);
  return dateTime ? dateTime.slice(0, 10) : fallback;
};

export const formatNumberJP = (value, fallback = '—') => {
  const num = Number(value);
  return Number.isFinite(num) ? num.toLocaleString('ja-JP') : fallback;
};

export const formatItemCount = (value, fallback = '—') => {
  const formatted = formatNumberJP(value, '');
  return formatted ? `${formatted}件` : fallback;
};

export const formatCurrencyJPY = (value, { prefix = '¥', suffix = '', fallback = '—' } = {}) => {
  const formatted = formatNumberJP(value, '');
  return formatted ? `${prefix}${formatted}${suffix}` : fallback;
};

/** 今日(ローカル)の 'YYYY-MM-DD' を返す（input[type="date"].min 用） */
export const todayYMD = () => {
  const d = new Date();
  const MM = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  return `${d.getFullYear()}-${MM}-${dd}`;
};

/** 日付文字列を 'YYYY/MM/DD' で表示（'YYYY-MM-DD...' を安全にスラッシュ区切りへ） */
export const toYmdSlash = (raw) => {
  const y = toDateInput(raw);
  return y ? y.replaceAll('-', '/') : '';
};

/** 'YYYY-MM-DD' を JST のその日 23:59:59（+09:00）に固定したオフセット付き文字列へ */
export const toJstEndOfDayOffset = (ymd) => {
  const y = toDateInput(ymd);
  return y ? `${y}T23:59:59+09:00` : '';
};


// サイドバー時刻ラベル（JST基準：今日=HH:mm / 昨日 / M.D / YYYY.M.D）
export const sidebarTimeLabelJST = (value) => {
  if (!value) return '';
  const s = String(value).trim();

  // ISO(UTC/オフセット付き)はそのままDateへ、TZなしはJSTとして解釈
  let d = new Date(s);
  if (isNaN(d) && /^(\d{4}-\d{2}-\d{2})[ T](\d{2}):(\d{2})/.test(s)) {
    d = new Date(s.replace(' ', 'T') + '+09:00');
  }
  if (isNaN(d)) return '';

  const parts = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false
  }).formatToParts(d);
  const take = (t) => parts.find(p => p.type === t)?.value ?? '';
  const yyyy = +take('year');
  const MM   = +take('month');
  const dd   = +take('day');
  const HH   = take('hour');
  const mm   = take('minute');

  const nowParts = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const takeNow = (t) => nowParts.find(p => p.type === t)?.value ?? '';
  const ny = +takeNow('year'), nm = +takeNow('month'), nd = +takeNow('day');

  const targetYmdUTC = Date.UTC(yyyy, MM - 1, dd);
  const todayYmdUTC  = Date.UTC(ny, nm - 1, nd);
  const diffDays = Math.round((todayYmdUTC - targetYmdUTC) / 86400000);

  if (diffDays === 0) return `${HH}:${mm}`;
  if (diffDays === 1) return '昨日';
  if (diffDays >= 2 && diffDays < 365) return `${MM}.${dd}`;
  return `${yyyy}.${MM}.${dd}`;
};

// ─────────────────────────────────────────────
// 単純な経過時間チェック（UTC/オフセット付きISO向け）
// ─────────────────────────────────────────────

export const isWithinHoursFromNow = (val, hours, nowMs = Date.now()) => {
  if (!val || !Number.isFinite(hours)) return false;
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return false;
  const diff = nowMs - d.getTime();
  if (diff < 0) return false;
  return diff <= hours * 60 * 60 * 1000;
};
